// src/pages/Payments.js
import React, { useEffect, useMemo, useState } from 'react'
import TablePagination from '@mui/material/TablePagination'
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'

import {
  fetchVehicles,
  fetchPayments,
  createPaymentReceipt,
  deletePaymentReceipt,
  updatePayment,
  fetchCarryForwardBalances,
} from '../apiClient'

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const formatDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '')
const money = (v) => num(v).toLocaleString() + ' TZS'

// ✅ idempotency key helper
const makeIdempotencyKey = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

// ✅ Adjust this to how you store auth meta in your app
function getAuthMeta() {
  const role =
    localStorage.getItem('role') ||
    localStorage.getItem('user_role') ||
    'user'

  let userId =
    localStorage.getItem('userId') ||
    localStorage.getItem('user_id') ||
    ''

  // fallback: Supabase session from sb-*-auth-token
  if (!userId) {
    const sbKey = Object.keys(localStorage).find(
      (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
    )
    if (sbKey) {
      try {
        const session = JSON.parse(localStorage.getItem(sbKey) || '{}')
        userId = session?.user?.id || session?.user?.sub || ''
      } catch (_) {}
    }
  }

  return { role, userId }
}

// ✅ row balance helper (per allocation row)
const getRowBalance = (p) => {
  const remaining = num(p.remaining_balance)
  if (remaining > 0) return { type: 'due', value: remaining }
  if (remaining < 0) return { type: 'over', value: Math.abs(remaining) }
  return { type: 'none', value: 0 }
}

const renderStatusChip = (p) => {
  const s = String(p.status || '').toLowerCase()
  const bal = getRowBalance(p)

  if (s === 'partial' || bal.type === 'due') {
    return <Chip size="small" label="PARTIAL" color="warning" />
  }
  if (s === 'late') return <Chip size="small" label="LATE" color="error" />
  if (s === 'paid') return <Chip size="small" label="PAID" color="success" />
  if (s === 'advance') return <Chip size="small" label="ADVANCE (Prepaid)" color="info" />

  return <Chip size="small" label={p.status || 'N/A'} variant="outlined" />
}

// ✅ grouped receipt status (simple & layman-friendly)
const renderReceiptStatusChip = (agg) => {
  if (agg.hasPartial) return <Chip size="small" label="PARTIAL" color="warning" />
  if (agg.hasLate) return <Chip size="small" label="LATE" color="error" />
  if (agg.hasAdvance) return <Chip size="small" label="ADVANCE" color="info" />
  return <Chip size="small" label="PAID" color="success" />
}

export default function PaymentsPage() {
  const { role, userId } = getAuthMeta()
  const isAdminView = role === 'admin' || role === 'super_admin'

  const [vehicles, setVehicles] = useState([])
  const [payments, setPayments] = useState([])
  const [walletMap, setWalletMap] = useState({})

  const [formData, setFormData] = useState({
    vehicle_id: '',
    amount: '',
    payment_date: '',
    note: '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [lastSubmitKey, setLastSubmitKey] = useState(null)

  // view mode: grouped (by receipt) OR detailed (allocation rows)
  const [viewMode, setViewMode] = useState('grouped')

  // edit states (detailed mode)
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})

  // deleting receipt guard
  const [deletingReceiptId, setDeletingReceiptId] = useState(null)

  const [searchTerm, setSearchTerm] = useState('')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // ---------- LOAD DATA ----------
  const loadData = async () => {
    try {
      const [vehRes, payRes, walletRes] = await Promise.all([
        fetchVehicles({ userId, role }),
        fetchPayments({ userId, role }),
        isAdminView
          ? fetchCarryForwardBalances({ userId, role })
          : Promise.resolve({ balances: [] }),
      ])

      setVehicles(vehRes.vehicles || [])
      setPayments(payRes.payments || [])

      if (isAdminView) {
        const map = {}
        ;(walletRes.balances || []).forEach((r) => {
          map[String(r.vehicle_id)] = num(r.balance)
        })
        setWalletMap(map)
      } else {
        setWalletMap({})
      }
    } catch (err) {
      console.error('[Payments] failed to load data:', err)
      setVehicles([])
      setPayments([])
      setWalletMap({})
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setPage(0)
  }, [searchTerm, filterFrom, filterTo, viewMode])

  // ---------- FORM ----------
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return

    if (!userId) {
      alert('Missing userId in localStorage/session. Please logout & login again.')
      return
    }

    if (!formData.vehicle_id) {
      alert('Please select a vehicle')
      return
    }

    const amount = num(formData.amount)
    if (!amount || amount <= 0) {
      alert('Please enter a valid amount')
      return
    }

    if (!formData.payment_date) {
      alert('Select payment date')
      return
    }

    setSubmitting(true)

    const idemKey = lastSubmitKey || makeIdempotencyKey()
    setLastSubmitKey(idemKey)

    try {
      const payload = {
        vehicle_id: Number(formData.vehicle_id),
        amount,
        received_at: formData.payment_date,
        note: formData.note || null,
        role,
        userId,
        created_by: userId,
        idempotency_key: idemKey,
      }

      const res = await createPaymentReceipt(payload)
      const wallet = num(res.wallet_balance ?? 0)

      alert(`Receipt saved. Wallet balance: ${wallet.toLocaleString()} TZS`)

      setFormData({ vehicle_id: '', amount: '', payment_date: '', note: '' })
      setLastSubmitKey(null)
      await loadData()
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message

      console.error('[Payments] createPaymentReceipt error:', err?.response?.data || err)
      alert(`Failed to record receipt: ${msg || 'Check console/network.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  // ---------- EDIT (detailed mode only) ----------
  const startEdit = (payment) => {
    setEditingId(payment.id)
    setEditData({
      amount: payment.amount ?? '',
      received_at: formatDate(payment.received_at),
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditData({})
  }

  const saveEdit = async (payment) => {
    const newAmount = num(editData.amount)
    const newReceivedAt = editData.received_at

    if (!newReceivedAt || !newAmount) {
      alert('Received At and amount are required')
      return
    }

    const oldAmount = num(payment.amount)
    const oldReceivedAt = formatDate(payment.received_at)

    if (newAmount === oldAmount && newReceivedAt === oldReceivedAt) {
      cancelEdit()
      return
    }

    try {
      await updatePayment(payment.id, {
        amount: newAmount,
        received_at: newReceivedAt,
      })

      alert('Edit saved')
      cancelEdit()
      await loadData()
    } catch (err) {
      console.error('[Payments] updatePayment error:', err)
      alert('Failed to save edit – check console/network.')
    }
  }

  // ✅ delete receipt (works for both grouped + detailed)
  const deleteReceipt = async (receiptId) => {
    if (!receiptId) return

    if (deletingReceiptId === receiptId) return
    if (!window.confirm(`Delete receipt #${receiptId} and rebuild allocations?`)) return

    try {
      setDeletingReceiptId(receiptId)
      await deletePaymentReceipt(receiptId, { role, userId })
      alert(`Receipt #${receiptId} deleted and allocations rebuilt.`)
      await loadData()
    } catch (err) {
      console.error('[Payments] delete receipt error:', err?.response?.data || err)
      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to delete receipt – check console/network.'
      )
    } finally {
      setDeletingReceiptId(null)
    }
  }

  // ---------- FILTERS (base filter on raw payments) ----------
  const baseFilteredPayments = useMemo(() => {
    return payments.filter((p) => {
      const plate = (p.plate_number || '').toLowerCase()
      const driver = (p.driver_name || '').toLowerCase()
      const term = searchTerm.toLowerCase()

      const matchesSearch = !term || plate.includes(term) || driver.includes(term)

      const received = formatDate(p.received_at)
      const covered = formatDate(p.payment_date)
      const dateToCompare = received || covered

      const matchesDate =
        (!filterFrom || (dateToCompare && dateToCompare >= filterFrom)) &&
        (!filterTo || (dateToCompare && dateToCompare <= filterTo))

      return matchesSearch && matchesDate
    })
  }, [payments, searchTerm, filterFrom, filterTo])

  // ---------- SUMMARY ----------
  const totalCollected = baseFilteredPayments.reduce((sum, p) => sum + num(p.amount), 0)

  const todayStr = dayjs().format('YYYY-MM-DD')
  const totalToday = baseFilteredPayments
    .filter((p) => formatDate(p.received_at) === todayStr)
    .reduce((sum, p) => sum + num(p.amount), 0)

  const today = dayjs().startOf('day')
  const expectedOverall = vehicles.reduce((sum, v) => {
    if (!v.contract_start) return sum

    const start = dayjs(v.contract_start).startOf('day')
    const endCap = v.contract_end ? dayjs(v.contract_end).startOf('day') : null
    const end = endCap ? (endCap.isBefore(today, 'day') ? endCap : today) : today

    if (end.isBefore(start, 'day')) return sum

    const days = end.diff(start, 'day') + 1
    return sum + days * num(v.daily_rate)
  }, 0)

  const remainingExpected = Math.max(0, expectedOverall - totalCollected)

  // ---------- WALLET ----------
  const walletTotal = useMemo(() => {
    if (!isAdminView) return 0
    return Object.values(walletMap).reduce((sum, b) => sum + num(b), 0)
  }, [walletMap, isAdminView])

  const selectedVehicleWallet = useMemo(() => {
    if (!isAdminView || !formData.vehicle_id) return 0
    return num(walletMap[String(formData.vehicle_id)] || 0)
  }, [walletMap, isAdminView, formData.vehicle_id])

  // ---------- GROUP BY RECEIPT (VIEW) ----------
  const groupedReceipts = useMemo(() => {
    const map = new Map()

    // only receipt rows
    const rows = baseFilteredPayments.filter((p) => p.receipt_id)

    for (const p of rows) {
      const rid = p.receipt_id
      if (!rid) continue

      if (!map.has(rid)) {
        map.set(rid, {
          receipt_id: rid,
          vehicle_id: p.vehicle_id,
          plate_number: p.plate_number,
          driver_name: p.driver_name,
          received_at: formatDate(p.received_at),
          amount_sum: 0,
          covered_from: formatDate(p.payment_date),
          covered_to: formatDate(p.payment_date),
          hasLate: false,
          hasAdvance: false,
          hasPartial: false,
          due_sum: 0,
          over_sum: 0,
        })
      }

      const agg = map.get(rid)
      agg.amount_sum += num(p.amount)

      const cov = formatDate(p.payment_date)
      if (cov && (!agg.covered_from || cov < agg.covered_from)) agg.covered_from = cov
      if (cov && (!agg.covered_to || cov > agg.covered_to)) agg.covered_to = cov

      const rcv = formatDate(p.received_at)
      // keep latest received_at (normally same)
      if (rcv && (!agg.received_at || rcv > agg.received_at)) agg.received_at = rcv

      const s = String(p.status || '').toLowerCase()
      if (s === 'late') agg.hasLate = true
      if (s === 'advance') agg.hasAdvance = true

      const bal = getRowBalance(p)
      if (s === 'partial' || bal.type === 'due') agg.hasPartial = true

      if (bal.type === 'due') agg.due_sum += num(bal.value)
      if (bal.type === 'over') agg.over_sum += num(bal.value)
    }

    // sort newest receipt first (by receipt id desc)
    return Array.from(map.values()).sort((a, b) => (b.receipt_id || 0) - (a.receipt_id || 0))
  }, [baseFilteredPayments])

  // detailed rows (allocation rows)
  const detailedRows = useMemo(() => {
    // newest first by received_at desc then id desc if you want:
    // but keep backend order if already good; here keep as-is:
    return baseFilteredPayments
  }, [baseFilteredPayments])

  // choose view rows
  const viewRows = viewMode === 'grouped' ? groupedReceipts : detailedRows

  const paginated = viewRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  // ---------- RENDER ----------
  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5">Record Payment</Typography>

        <Box display="flex" gap={1} alignItems="center">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, v) => {
              if (v) {
                setEditingId(null)
                setEditData({})
                setViewMode(v)
              }
            }}
          >
            <ToggleButton value="grouped">Grouped</ToggleButton>
            <ToggleButton value="detailed">Detailed</ToggleButton>
          </ToggleButtonGroup>

          {isAdminView && (
            <Chip
              label={`Wallet Balance (unallocated): ${walletTotal.toLocaleString()} TZS`}
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* FORM */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="vehicle-label">Select Vehicle</InputLabel>
                <Select
                  labelId="vehicle-label"
                  label="Select Vehicle"
                  name="vehicle_id"
                  value={formData.vehicle_id}
                  onChange={handleChange}
                >
                  {vehicles.map((v) => {
                    const bal = isAdminView ? num(walletMap[String(v.id)] || 0) : 0
                    return (
                      <MenuItem key={v.id} value={String(v.id)}>
                        {v.plate_number} - {v.driver_name}
                        {isAdminView && bal > 0 ? ` (Wallet: ${bal.toLocaleString()} TZS)` : ''}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>

              {isAdminView && formData.vehicle_id && selectedVehicleWallet > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Selected vehicle wallet: {selectedVehicleWallet.toLocaleString()} TZS
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="amount"
                label="Amount"
                type="number"
                fullWidth
                value={formData.amount}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="payment_date"
                label="Payment Received On"
                type="date"
                fullWidth
                value={formData.payment_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                name="note"
                label="Note (optional)"
                fullWidth
                value={formData.note}
                onChange={handleChange}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <Button type="submit" variant="contained" fullWidth disabled={submitting}>
                {submitting ? 'SAVING...' : 'SUBMIT PAYMENT'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* SUMMARY */}
      <Box sx={{ mb: 2 }}>
        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="h6" gutterBottom>
            <strong>Summary</strong>
          </Typography>

          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="body1">
                <strong>Total Collected:</strong> {totalCollected.toLocaleString()} TZS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Collected Today:</strong> {totalToday.toLocaleString()} TZS
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Expected Overall:</strong> {expectedOverall.toLocaleString()} TZS
              </Typography>
              <Typography variant="body2" color="error">
                <strong>Remaining Expected:</strong> {remainingExpected.toLocaleString()} TZS
              </Typography>
            </Grid>

            {isAdminView && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Unallocated Wallet (Admin Only):</strong> {walletTotal.toLocaleString()} TZS
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Wallet is only leftover money that cannot fit into any contract day. Prepaid money should show as ADVANCE rows.
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </Box>

      {/* FILTERS */}
      <Grid container spacing={2} alignItems="center" mb={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Search by Plate or Driver"
            fullWidth
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="From (Received/Covered)"
            type="date"
            fullWidth
            size="small"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} md={4}>
          <TextField
            label="To (Received/Covered)"
            type="date"
            fullWidth
            size="small"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      {/* TABLE */}
      <Typography variant="h6" gutterBottom>
        Payments {viewMode === 'grouped' ? '(Grouped by Receipt)' : '(Detailed)'}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Receipt</strong></TableCell>
              <TableCell><strong>Date Covered</strong></TableCell>
              <TableCell><strong>Received At</strong></TableCell>
              <TableCell><strong>Plate</strong></TableCell>
              <TableCell><strong>Driver</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Balance</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {/* GROUPED MODE */}
            {viewMode === 'grouped' &&
              paginated.map((r) => {
                const receiptId = r.receipt_id
                const deleting = deletingReceiptId === receiptId

                const coveredLabel =
                  r.covered_from && r.covered_to
                    ? (r.covered_from === r.covered_to
                        ? r.covered_from
                        : `${r.covered_from} → ${r.covered_to}`)
                    : '-'

                return (
                  <TableRow key={`receipt-${receiptId}`}>
                    <TableCell>
                      <Chip size="small" label={`#${receiptId}`} color="primary" variant="outlined" />
                    </TableCell>

                    <TableCell>{coveredLabel}</TableCell>
                    <TableCell>{r.received_at || '-'}</TableCell>
                    <TableCell>{r.plate_number}</TableCell>
                    <TableCell>{r.driver_name}</TableCell>
                    <TableCell>{money(r.amount_sum)}</TableCell>

                    <TableCell>{renderReceiptStatusChip(r)}</TableCell>

                    <TableCell>
                      {r.due_sum > 0 ? (
                        <Typography variant="body2" color="error">
                          Due: {money(r.due_sum)}
                        </Typography>
                      ) : r.over_sum > 0 ? (
                        <Typography variant="body2" color="success.main">
                          Over: {money(r.over_sum)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip title={`Delete receipt #${receiptId} and rebuild`}>
                        <span>
                          <IconButton
                            onClick={() => deleteReceipt(receiptId)}
                            disabled={deleting}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}

            {/* DETAILED MODE */}
            {viewMode === 'detailed' &&
              paginated.map((p) => {
                const receiptId = p.receipt_id
                const isManual = !receiptId

                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      {isManual ? (
                        <Chip size="small" label="MANUAL" variant="outlined" />
                      ) : (
                        <Chip size="small" label={`#${receiptId}`} color="primary" variant="outlined" />
                      )}
                    </TableCell>

                    <TableCell>{formatDate(p.payment_date)}</TableCell>

                    <TableCell>
                      {editingId === p.id ? (
                        <TextField
                          type="date"
                          size="small"
                          value={editData.received_at || ''}
                          onChange={(e) =>
                            setEditData((prev) => ({ ...prev, received_at: e.target.value }))
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        formatDate(p.received_at)
                      )}
                    </TableCell>

                    <TableCell>{p.plate_number}</TableCell>
                    <TableCell>{p.driver_name}</TableCell>

                    <TableCell>
                      {editingId === p.id ? (
                        <TextField
                          type="number"
                          size="small"
                          value={editData.amount}
                          onChange={(e) =>
                            setEditData((prev) => ({ ...prev, amount: e.target.value }))
                          }
                        />
                      ) : (
                        money(p.amount)
                      )}
                    </TableCell>

                    <TableCell>{renderStatusChip(p)}</TableCell>

                    <TableCell>
                      {(() => {
                        const b = getRowBalance(p)
                        if (b.type === 'due') {
                          return (
                            <Typography variant="body2" color="error">
                              Due: {money(b.value)}
                            </Typography>
                          )
                        }
                        if (b.type === 'over') {
                          return (
                            <Typography variant="body2" color="success.main">
                              Over: {money(b.value)}
                            </Typography>
                          )
                        }
                        return (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )
                      })()}
                    </TableCell>

                    <TableCell>
                      {editingId === p.id ? (
                        <>
                          <IconButton onClick={() => saveEdit(p)}>
                            <SaveIcon />
                          </IconButton>
                          <IconButton onClick={cancelEdit}>
                            <CancelIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton onClick={() => startEdit(p)}>
                            <EditIcon />
                          </IconButton>

                          <Tooltip
                            title={
                              isManual
                                ? 'Manual row: this page deletes receipts only'
                                : `Delete receipt #${receiptId} and rebuild`
                            }
                          >
                            <span>
                              <IconButton
                                onClick={() => (!isManual ? deleteReceipt(receiptId) : null)}
                                disabled={isManual || deletingReceiptId === receiptId}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}

            {paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No payments
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={viewRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10))
          setPage(0)
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </Box>
  )
}